import {
  Box,
  Stack,
  Grid2 as Grid,
  Button,
  Typography,
  Switch,
  Tooltip,
} from '@mui/material';
import storeConnector from '@src/store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import useSubscription from '@src/hooks/useSubscription';
import { ControlModes } from '@src/types/Stacks';
import EditIcon from '@mui/icons-material/Edit';
import { useState, useCallback, useMemo } from 'react';
import TemplatesFields from './TemplatesFields';
import { useForm, FormProvider } from 'react-hook-form';
import cloneDeep from 'lodash.clonedeep';
import TemplatesEnableConfirm from './TemplatesEnableConfirm';
import TemplatesFullConfig from './TemplatesFullConfig';
import { templatesViewSx } from '@src/components/_shared/sxStyles/Config/templates';
import { getQuickConfigFields } from '../../utils';
import { useConfigContext } from '@src/pages/Home/pages/Config/ConfigWrapperContext';
import TemplatesCardStatuses from './TemplatesCardStatuses';
import { generateDataPoint } from '@src/utils/generateTopic';
import { ControlAuthorities } from '@src/types/ControlModes';
import FormattedAlias from '@src/components/_shared/Translations/FormattedAlias';
import useAlias from '@alias';

interface TemplatesCardProps {
  controlMode: ControlModes;
  siteMeta: SiteMeta;
  handleUpdateStack: (controlMode: ControlModes) => void;
  isDefault?: boolean;
}

export interface FieldState {
  [fieldName: string]: {
    value: string | number | boolean;
    type: string;
    quickConfig?: boolean;
  };
}

export type FormValues = {
  [fieldName: string]: string | number | boolean;
};

function TemplatesCard({
  controlMode,
  siteMeta,
  handleUpdateStack,
  isDefault,
}: TemplatesCardProps) {
  const alias = useAlias();
  const defaultValues: FormValues = {};
  const { controlAuthority } = useConfigContext();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  controlMode.sections.forEach((section) => {
    if (section.fields) {
      section.fields.forEach((field) => {
        const fieldName = field.controllerField;
        defaultValues[fieldName] = field.value;
      });
    }
  });

  const quickConfigFields = useMemo(
    () => getQuickConfigFields(controlMode),
    [controlMode],
  );

  const statusTypes = useMemo(
    () => [
      {
        key: 'scada',
        pointName: `Disable_${controlMode.name}_SCADA`,
        displayName: `${controlMode.name} SCADA`,
        tooltipMessage: `${alias('Mode status that is set by SCADA commands.')} ${controlAuthority === ControlAuthorities.scada ? alias('The authority is set to SCADA. The status can be enabled/disabled only from the customer SCADA system.') : controlAuthority !== undefined && controlAuthority !== null ? `${alias('The authority is set to')} ${ControlAuthorities[controlAuthority].toUpperCase()}. ${alias('Switch to SCADA authority to enable/disable modes from the customer SCADA system.')}` : alias('The authority is not set')}`,
      },
      {
        key: 'hmi',
        pointName: `Disable_${controlMode.name}_Fractal`,
        displayName: `${controlMode.name} HMI`,
        tooltipMessage: `${alias('Mode status that is set by the HMI user.')} ${controlAuthority === ControlAuthorities.hmi ? alias('The authority is set to HMI. The status can be enabled/disabled from this page.') : controlAuthority !== undefined && controlAuthority !== null ? `${alias('The authority is set to')} ${ControlAuthorities[controlAuthority].toUpperCase()}. ${alias('Switch to HMI authority to enable/disable modes from this page.')}` : alias('The authority is not set')}`,
      },
      {
        key: 'active',
        pointName: `Disable_${controlMode.name}`,
        displayName: `${controlMode.name} ${alias('Disable')}`,
        tooltipMessage:
          controlAuthority !== undefined && controlAuthority !== null
            ? `${alias('Current run status of the control mode. The authority is set to')} ${ControlAuthorities[controlAuthority].toUpperCase()} ${alias('and the status is set by')} ${ControlAuthorities[controlAuthority].toUpperCase()} ${alias('Status')}.`
            : alias(
                'Current run status of the control mode. The authority is not set.',
              ),
      },
    ],
    [controlMode.name, controlAuthority, alias],
  );

  const subscriptionConfig = useMemo(
    () =>
      statusTypes.map(({ pointName }) =>
        generateDataPoint(
          {
            deviceType: 'site',
            controllerId: siteMeta.siteId,
            pointName,
            dataType: 'integer',
          },
          siteMeta.siteId,
        ),
      ),
    [statusTypes, siteMeta.siteId],
  );

  const subscriptionsWithValues = useSubscription(subscriptionConfig);

  const { scadaStatus, activeStatus, hmiStatus } = useMemo(() => {
    const result: Record<string, string | number | null | undefined> = {};
    statusTypes.forEach(({ key, pointName }) => {
      const subscription = subscriptionsWithValues.find(
        (s) => s.pointName === pointName,
      );
      result[`${key}Status`] = subscription?.value;
    });
    return result as {
      scadaStatus: string | number | null | undefined;
      activeStatus: string | number | null | undefined;
      hmiStatus: string | number | null | undefined;
    };
  }, [statusTypes, subscriptionsWithValues]);

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resetOptions: {
      keepDirtyValues: false,
    },
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const mapFormValuesToControlMode = useCallback(
    (formValues: FormValues) => {
      const updatedControlMode = cloneDeep(controlMode);

      updatedControlMode.sections.forEach((section) => {
        if (section.fields) {
          section.fields.forEach((field) => {
            const fieldName = field.controllerField;
            if (fieldName in formValues) {
              field.value =
                field.type === 'boolean'
                  ? formValues[fieldName] === 1
                    ? true
                    : false
                  : formValues[fieldName];
            }
          });
        }
      });

      return updatedControlMode;
    },
    [controlMode],
  );

  const onSubmit = useCallback(
    (formValues: FormValues) => {
      const updatedControlMode = mapFormValuesToControlMode(formValues);
      handleUpdateStack(updatedControlMode);
      setOpen(false);
    },
    [mapFormValuesToControlMode, handleUpdateStack],
  );

  const handleToggleUpdate = (_: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmOpen(true);
  };

  return (
    <FormProvider {...methods}>
      <Grid container spacing={2}>
        <Grid size={4} sx={templatesViewSx.templateNameGrid}>
          <Typography sx={templatesViewSx.templateName}>
            {controlMode.stackAlias ?? controlMode.name}
          </Typography>
          {isDefault && (
            <TemplatesCardStatuses
              scadaStatus={scadaStatus}
              activeStatus={activeStatus}
              hmiStatus={hmiStatus}
              statusTypes={statusTypes}
            />
          )}
        </Grid>
        <Grid size={8}>
          <Stack
            direction={'row'}
            sx={templatesViewSx.configWrapper}
            spacing={2}
          >
            <Box display={'flex'} alignItems='center'>
              <Box>
                <Tooltip
                  title={alias(
                    'Must be in HMI mode in order to enable/disable.',
                  )}
                  arrow
                  placement='top'
                  disableHoverListener={
                    controlAuthority !== ControlAuthorities.scada
                  }
                >
                  <Box>
                    <Switch
                      checked={activeStatus === 0}
                      onChange={handleToggleUpdate}
                      disabled={
                        controlAuthority === ControlAuthorities.scada ||
                        controlAuthority === ControlAuthorities.calendar
                      }
                    />
                  </Box>
                </Tooltip>
              </Box>
            </Box>
            <Stack spacing={3} flexGrow={1}>
              <TemplatesFields inputFields={quickConfigFields} />
              <Stack
                direction='row'
                spacing={2}
                sx={templatesViewSx.configWrapper.buttonWrapper}
              >
                {quickConfigFields.length > 0 && (
                  <Button
                    variant='contained'
                    color='primary'
                    size='small'
                    type='submit'
                    onClick={methods.handleSubmit(onSubmit)}
                  >
                    <FormattedAlias id='Quick Save' />
                  </Button>
                )}
                <Button
                  variant='outlined'
                  color='inherit'
                  size='small'
                  startIcon={<EditIcon />}
                  onClick={handleOpen}
                >
                  <FormattedAlias id='Full Config' />
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        {open && (
          <TemplatesFullConfig
            setOpen={setOpen}
            controlMode={controlMode}
            onSubmit={onSubmit}
          />
        )}

        {confirmOpen && (
          <TemplatesEnableConfirm
            setConfirmOpen={setConfirmOpen}
            modeName={controlMode.name}
            activeStatus={activeStatus}
          />
        )}
      </Grid>
    </FormProvider>
  );
}

export default storeConnector(TemplatesCard, {
  config: ['siteMeta'],
});
